/**
 * Font Size Manager
 * Manages scalable text sizes for accessibility
 * Supports WCAG 2.1 AA requirement for 200% text scaling
 */

export type FontSizeLevel = 'small' | 'medium' | 'large' | 'xlarge';

export interface FontSizes {
  body: number;
  heading: number;
  label: number;
  small: number;
  title: number;
  button: number;
}

// Font size configurations for each level
const FONT_SIZE_CONFIGS: Record<FontSizeLevel, FontSizes> = {
  small: {
    body: 14,
    heading: 20,
    label: 12,
    small: 10,
    title: 28,
    button: 13,
  },
  medium: {
    body: 16,
    heading: 24,
    label: 14,
    small: 12,
    title: 32,
    button: 14,
  },
  large: {
    body: 20,
    heading: 30,
    label: 18,
    small: 14,
    title: 40,
    button: 18,
  },
  xlarge: {
    body: 24,
    heading: 36,
    label: 22,
    small: 18,
    title: 48,
    button: 22,
  },
};

// CSS custom properties are applied dynamically in applyFontSizes()

export class FontSizeManager {
  private currentLevel: FontSizeLevel = 'medium';
  private styleElement: HTMLStyleElement | null = null;
  private listeners: Set<(sizes: FontSizes) => void> = new Set();

  constructor() {
    this.createStyleElement();
    this.applyFontSizes();
  }

  private createStyleElement(): void {
    if (typeof document === 'undefined') return;

    this.styleElement = document.createElement('style');
    this.styleElement.id = 'accessibility-font-sizes';
    document.head.appendChild(this.styleElement);
  }

  setSize(level: FontSizeLevel): void {
    this.currentLevel = level;
    this.applyFontSizes();
    this.notifyListeners();
  }

  getSize(): FontSizeLevel {
    return this.currentLevel;
  }

  getSizes(): FontSizes {
    return { ...FONT_SIZE_CONFIGS[this.currentLevel] };
  }

  // Get size for a specific type
  getBodySize(): number {
    return FONT_SIZE_CONFIGS[this.currentLevel].body;
  }

  getHeadingSize(): number {
    return FONT_SIZE_CONFIGS[this.currentLevel].heading;
  }

  getLabelSize(): number {
    return FONT_SIZE_CONFIGS[this.currentLevel].label;
  }

  getSmallSize(): number {
    return FONT_SIZE_CONFIGS[this.currentLevel].small;
  }

  getTitleSize(): number {
    return FONT_SIZE_CONFIGS[this.currentLevel].title;
  }

  getButtonSize(): number {
    return FONT_SIZE_CONFIGS[this.currentLevel].button;
  }

  // Get scale factor relative to medium (base)
  getScaleFactor(): number {
    const baseSizes = FONT_SIZE_CONFIGS.medium;
    const currentSizes = FONT_SIZE_CONFIGS[this.currentLevel];
    return currentSizes.body / baseSizes.body;
  }

  // Scale a custom font size by the current factor
  scaleSize(baseSize: number): number {
    return Math.round(baseSize * this.getScaleFactor());
  }

  private applyFontSizes(): void {
    if (typeof document === 'undefined' || !this.styleElement) return;

    const sizes = FONT_SIZE_CONFIGS[this.currentLevel];

    // Generate CSS with custom properties
    this.styleElement.textContent = `
      :root {
        --font-size-body: ${sizes.body}px;
        --font-size-heading: ${sizes.heading}px;
        --font-size-label: ${sizes.label}px;
        --font-size-small: ${sizes.small}px;
        --font-size-title: ${sizes.title}px;
        --font-size-button: ${sizes.button}px;
        --font-scale-factor: ${this.getScaleFactor()};
      }

      /* Apply to common HTML elements */
      body {
        font-size: var(--font-size-body);
      }

      h1, .heading-1 {
        font-size: var(--font-size-title);
      }

      h2, h3, .heading-2, .heading-3 {
        font-size: var(--font-size-heading);
      }

      label, .label {
        font-size: var(--font-size-label);
      }

      small, .small {
        font-size: var(--font-size-small);
      }

      button, .button {
        font-size: var(--font-size-button);
      }

      /* Accessibility-specific styles */
      .a11y-text-body {
        font-size: var(--font-size-body) !important;
      }

      .a11y-text-heading {
        font-size: var(--font-size-heading) !important;
      }

      .a11y-text-label {
        font-size: var(--font-size-label) !important;
      }

      .a11y-text-small {
        font-size: var(--font-size-small) !important;
      }

      .a11y-text-title {
        font-size: var(--font-size-title) !important;
      }
    `;
  }

  // Subscribe to font size changes
  onChange(listener: (sizes: FontSizes) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    const sizes = this.getSizes();
    this.listeners.forEach((listener) => listener(sizes));
  }

  // Get font size string for Phaser text
  getPhaserFontSize(type: keyof FontSizes): string {
    return `${FONT_SIZE_CONFIGS[this.currentLevel][type]}px`;
  }

  // Get all levels for UI selection
  static getLevels(): { value: FontSizeLevel; label: string; description: string }[] {
    return [
      { value: 'small', label: 'Small', description: '87.5% scale' },
      { value: 'medium', label: 'Medium', description: '100% scale (default)' },
      { value: 'large', label: 'Large', description: '125% scale' },
      { value: 'xlarge', label: 'Extra Large', description: '150% scale' },
    ];
  }

  // Check if current size meets minimum requirements
  meetsWCAGRequirements(): boolean {
    const sizes = this.getSizes();
    // WCAG recommends minimum 16px for body text
    return sizes.body >= 14;
  }

  // Clean up
  destroy(): void {
    if (this.styleElement && this.styleElement.parentNode) {
      this.styleElement.parentNode.removeChild(this.styleElement);
    }
    this.listeners.clear();
  }
}
